interface ServicesOverviewProps {
  onOpenConsultation: () => void;
}

const SERVICES = [
  {
    icon: 'ri-user-heart-line',
    title: 'Personal Care Assistance',
    desc: 'Bathing, grooming, dressing, and mobility support — always maintaining dignity and independence.',
    href: '/services/personal-care',
    image: 'https://public.readdy.ai/ai/img_res/55f1b348e90f45e21d134bc5a08cb075.jpg',
    tag: 'Most Requested',
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'Companion Care',
    desc: 'Meaningful social engagement, emotional support, and joyful companionship every day.',
    href: '/services/companion-care',
  },
  {
    icon: 'ri-brain-line',
    title: "Alzheimer's & Dementia Care",
    desc: 'Specialized memory care from trained, patient, and compassionate caregivers.',
    href: '/services/alzheimers-dementia-care',
  },
  {
    icon: 'ri-rest-time-line',
    title: 'Respite Care',
    desc: 'Flexible relief for family caregivers — schedule a few hours or a full week.',
    href: '/services/respite-care',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Post-Hospital Recovery',
    desc: 'Safe, supervised recovery at home after surgery or any hospitalization.',
    href: '/services/post-hospital-recovery',
  },
];

export default function ServicesOverview({ onOpenConsultation }: ServicesOverviewProps) {
  return (
    <section className="py-28 bg-[#F7F9F8]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-gold"></span>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.18em]">What We Offer</p>
            </div>
            <h2 className="font-heading text-navy font-bold leading-tight" style={{ fontSize: 'clamp(2rem, 3.5vw, 3.2rem)' }}>
              In-Home Senior Care<br className="hidden lg:block" /> Services in Oakland, IL
            </h2>
          </div>
          <div className="flex flex-col gap-2 items-start lg:items-end max-w-xs">
            <p className="text-gray-500 text-sm leading-relaxed text-left lg:text-right">
              Professional care tailored to every stage of aging — in the comfort of home.
            </p>
            <button
              onClick={onOpenConsultation}
              className="whitespace-nowrap flex items-center gap-2 text-navy font-bold text-sm hover:text-gold transition-colors cursor-pointer group"
            >
              View All Services
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Featured card — portrait image */}
          <a
            href="/services/personal-care"
            className="lg:row-span-2 relative overflow-hidden rounded-2xl group min-h-[460px] block"
          >
            <img
              src={SERVICES[0].image}
              alt="Personal care assistance for seniors"
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44] via-[#0F2A44]/55 to-[#0F2A44]/10" />
            <div className="absolute inset-x-0 bottom-0 p-8 z-10">
              <div className="inline-flex items-center gap-1.5 bg-gold/25 border border-gold/40 text-gold text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
                <i className="ri-star-fill text-xs"></i>
                {SERVICES[0].tag}
              </div>
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm mb-4">
                <i className="ri-user-heart-line text-white text-xl"></i>
              </div>
              <h3 className="font-heading text-white text-2xl font-bold mb-3 leading-tight">
                {SERVICES[0].title}
              </h3>
              <p className="text-white/65 text-sm leading-relaxed mb-5">
                Compassionate support with bathing, grooming, dressing, and daily activities — preserving dignity at every step.
              </p>
              <span className="inline-flex items-center gap-2 text-gold text-sm font-bold group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </a>

          {/* Other service cards */}
          {SERVICES.slice(1).map((service) => (
            <a
              key={service.title}
              href={service.href}
              className="group flex flex-col bg-white rounded-2xl p-7 border border-gray-100/80 hover:border-[#8FAF9B] hover:-translate-y-1 transition-all relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-28 h-28 bg-sage/5 rounded-full -translate-y-10 translate-x-10 group-hover:bg-sage/10 transition-colors pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-gold/4 rounded-full translate-y-6 -translate-x-6 pointer-events-none" />
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-sage/15 mb-5 group-hover:bg-sage/25 transition-colors">
                <i className={`${service.icon} text-sage text-xl`}></i>
              </div>
              <h3 className="font-heading text-navy text-lg font-bold mb-2.5 leading-tight">{service.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{service.desc}</p>
              <span className="flex items-center gap-2 text-gold text-sm font-bold group-hover:gap-3 transition-all">
                Explore Service <i className="ri-arrow-right-line"></i>
              </span>
            </a>
          ))}
        </div>

        {/* Mid-section CTA strip */}
        <div className="mt-14 rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(120deg, #0F2A44 0%, #163650 50%, #0d3528 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(200,169,106,0.3) 0%, transparent 60%)' }} />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-9 py-8">
            <div>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.18em] mb-2">Free, No-Obligation</p>
              <h3 className="font-heading text-white text-2xl font-bold leading-tight">Not sure which service is right?</h3>
              <p className="text-white/55 text-sm mt-1.5">
                Our care coordinators will guide your family to the perfect care plan.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 flex-shrink-0">
              <button
                onClick={onOpenConsultation}
                className="whitespace-nowrap bg-gold text-navy font-bold px-7 py-3.5 rounded-full hover:brightness-110 transition-all cursor-pointer text-sm"
              >
                Schedule Consultation
              </button>
              <a
                href="tel:2173462042"
                className="whitespace-nowrap flex items-center gap-2 bg-white/10 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/20 transition-all text-sm border border-white/20"
              >
                <i className="ri-phone-fill text-gold"></i>
                (217) 346-2042
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
